import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, Sparkles, Shield, Zap, Users, Camera, Brain, FileText, MessageCircle, CreditCard, Loader2 } from 'lucide-react';
import { API } from '@/lib/api';

export default function PricingPage() {
  const { language } = useLanguage();
  const { user, getHeaders, updateUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const de = language === 'de';
  const [billing, setBilling] = useState('monthly');
  const [purchasing, setPurchasing] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') pollAboStatus(sessionId);
    else if (status === 'cancelled') toast.error(de ? 'Zahlung abgebrochen' : 'Payment cancelled');
  }, []);

  const pollAboStatus = async (sessionId, attempt = 0) => {
    setPolling(true);
    try {
      const res = await axios.get(`${API}/abo/payment-status/${sessionId}`, { headers: getHeaders() });
      if (res.data.payment_status === 'paid') {
        toast.success(de ? 'Abo aktiviert! Willkommen!' : 'Subscription activated! Welcome!');
        const me = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
        updateUser(me.data);
        setPolling(false);
        return;
      }
      if (attempt < 5) setTimeout(() => pollAboStatus(sessionId, attempt + 1), 2000);
      else setPolling(false);
    } catch { setPolling(false); }
  };

  const handlePurchase = async (aboId) => {
    if (!user) { nav('/auth?mode=register'); return; }
    setPurchasing(aboId);
    try {
      const res = await axios.post(`${API}/abo/purchase`, {
        abo_id: aboId,
        origin_url: window.location.origin,
      }, { headers: getHeaders() });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setPurchasing(null);
  };

  const plans = [
    {
      name: de ? 'Gratis' : 'Free',
      price: { monthly: '0', yearly: '0' },
      period: de ? '/Monat' : '/month',
      desc: de ? '7 Tage testen – alle Basis-Features' : '7-day trial – all basic features',
      highlight: false,
      features: de ? [
        'Basis-Ernährungs-Tracking (3 Mahlzeiten/Tag)',
        'Einfaches Symptom-Logging',
        'Zyklus-Kalender (Basis)',
        '1x/Woche Heatmap-Vorschau',
        'Glow Score Vorschau',
        '3 Micro-Learning Videos',
      ] : [
        'Basic nutrition tracking (3 meals/day)',
        'Simple symptom logging',
        'Cycle calendar (basic)',
        '1x/week heatmap preview',
        'Glow Score preview',
        '3 micro-learning videos',
      ],
    },
    {
      name: 'Basis',
      price: { monthly: '29', yearly: '249' },
      period: billing === 'monthly' ? (de ? '/Monat' : '/month') : (de ? '/Jahr' : '/year'),
      yearSave: de ? 'Spare 28%' : 'Save 28%',
      desc: de ? 'Alle 5 Module + KI-Multi-Experten-Coach' : 'All 5 modules + AI multi-expert coach',
      highlight: false,
      features: de ? [
        'Unbegrenztes Foto-Mahlzeiten-Tracking',
        'KI-Multi-Experten-Coach (Ernährung + Hormone)',
        'Volle Symptom-Heatmap (Zyklus x Ernährung)',
        'Selfie Glow-Analyse + Glow Score',
        'Tägliche "Next Best Actions"',
        'Hashimoto TSH-Tracking + Selen-Optimierung',
        'Perimenopause Symptom-Korrelationen',
        'Komplette Video-Bibliothek (20+ Module)',
        'Wöchentliche PDF-Reports',
        'Medikamenten-Erinnerungen',
      ] : [
        'Unlimited photo meal tracking',
        'AI multi-expert coach (nutrition + hormones)',
        'Full symptom heatmap (cycle x nutrition)',
        'Selfie glow analysis + Glow Score',
        'Daily "Next Best Actions"',
        'Hashimoto TSH tracking + selenium optimization',
        'Perimenopause symptom correlations',
        'Complete video library (20+ modules)',
        'Weekly PDF reports',
        'Medication reminders',
      ],
    },
    {
      name: 'Premium',
      price: { monthly: '299', yearly: '699' },
      period: billing === 'monthly' ? (de ? '/3 Monate' : '/3 months') : (de ? '/Jahr' : '/year'),
      yearSave: de ? 'Spare 42%' : 'Save 42%',
      desc: de ? 'Belinda begleitet dich persönlich' : 'Belinda guides you personally',
      highlight: true,
      features: de ? [
        'Alles aus Basis +',
        'Persönlicher Async-Chat mit Belinda (max. 24h)',
        'Dein individueller Ernährungs- & Lifestyle-Plan',
        'Wöchentliche 1:1 Video-Calls mit Belinda (15 Min)',
        'Terminbuchung direkt in der App',
        'Exklusives Gruppen-Coaching (max. 10 Frauen)',
        'Persönlicher 12-Wochen-Transformationsplan',
        'Zugang zu belosophie LIFE (Stil, Coaching, Wohnen)',
        'Prioritäts-Support',
      ] : [
        'Everything in Basis +',
        'Personal async chat with Belinda (max 24h)',
        'Your individual nutrition & lifestyle plan',
        'Weekly 1:1 video calls with Belinda (15 min)',
        'Book appointments directly in the app',
        'Exclusive group coaching (max 10 women)',
        'Personal 12-week transformation plan',
        'Access to belosophie LIFE (style, coaching, living)',
        'Priority support',
      ],
    },
  ];

  const comparisons = [
    { feature: de ? 'Ernährungs-Tracking' : 'Nutrition Tracking', icon: Camera, free: de ? '3/Tag' : '3/day', basis: de ? 'Unbegrenzt' : 'Unlimited', premium: de ? 'Unbegrenzt' : 'Unlimited' },
    { feature: de ? 'KI-Coach' : 'AI Coach', icon: Brain, free: '-', basis: true, premium: true },
    { feature: de ? 'Symptom-Heatmap' : 'Symptom Heatmap', icon: Zap, free: de ? 'Vorschau' : 'Preview', basis: true, premium: true },
    { feature: de ? 'Selfie Glow-Analyse' : 'Selfie Glow Analysis', icon: Sparkles, free: '-', basis: true, premium: true },
    { feature: de ? 'Hashimoto-Modul' : 'Hashimoto Module', icon: Shield, free: de ? 'Basis' : 'Basic', basis: true, premium: true },
    { feature: de ? 'PDF-Reports für Arzt' : 'PDF Reports for Doctor', icon: FileText, free: '-', basis: true, premium: true },
    { feature: de ? 'Persönlicher Chat' : 'Personal Chat', icon: MessageCircle, free: '-', basis: '-', premium: true },
    { feature: de ? 'Live Video-Calls' : 'Live Video Calls', icon: Users, free: '-', basis: '-', premium: '4x' },
  ];

  return (
    <div className="min-h-screen bg-bone" data-testid="pricing-page">
      {/* Payment Status */}
      {polling && (
        <div className="max-w-7xl mx-auto px-6 pt-8">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
            <Loader2 size={18} className="text-green-600 animate-spin" />
            <p className="font-body text-sm text-green-700">{de ? 'Zahlung wird bestätigt... Abo wird aktiviert.' : 'Confirming payment... Activating subscription.'}</p>
          </div>
        </div>
      )}
      {/* Back nav */}
      <div className="max-w-7xl mx-auto px-6 pt-8">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground font-body text-sm transition-colors" data-testid="pricing-back-btn">
          <ArrowLeft size={16} />
          <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8 text-center">
        <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">PRICING</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight">
          {de ? 'Wähle deinen Weg' : 'Choose Your Path'}
        </h1>
        <p className="font-body text-base md:text-lg text-foreground/50 mt-4 max-w-2xl mx-auto">
          {de
            ? 'Starte gratis. Upgrade, wenn du merkst: Das ist speziell für Frauen in deiner Lebensphase gemacht.'
            : 'Start free. Upgrade when you realize: This is specifically made for women in your life phase.'}
        </p>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mt-8" data-testid="billing-toggle">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-5 py-2 rounded-full font-body text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-terracotta text-white' : 'bg-white text-foreground/60 border border-border/60'}`}
            data-testid="billing-monthly"
          >
            {de ? 'Monatlich' : 'Monthly'}
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-5 py-2 rounded-full font-body text-sm font-medium transition-all ${billing === 'yearly' ? 'bg-terracotta text-white' : 'bg-white text-foreground/60 border border-border/60'}`}
            data-testid="billing-yearly"
          >
            {de ? 'Jährlich' : 'Yearly'}
            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{de ? 'Spare bis 55%' : 'Save up to 55%'}</span>
          </button>
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="pricing-plans">
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, i) => (
            <Card
              key={i}
              className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${plan.highlight ? 'bg-sage text-white border-sage shadow-xl shadow-sage/20 scale-[1.02]' : 'bg-white border border-border/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]'}`}
              data-testid={`plan-card-${i}`}
            >
              {plan.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terracotta text-white border-0 rounded-full px-4 py-1">
                  {de ? 'Beliebt' : 'Popular'}
                </Badge>
              )}
              <h3 className="font-display text-2xl font-medium mb-2">{plan.name}</h3>
              <p className={`font-body text-sm mb-6 ${plan.highlight ? 'text-white/70' : 'text-foreground/60'}`}>{plan.desc}</p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-body text-sm">{"\u20ac"}</span>
                <span className="font-display text-5xl font-semibold">
                  {billing === 'yearly' ? plan.price.yearly : plan.price.monthly}
                </span>
                <span className={`font-body text-sm ${plan.highlight ? 'text-white/60' : 'text-foreground/40'}`}>
                  {billing === 'yearly' && i > 0 ? (de ? '/Jahr' : '/year') : plan.period}
                </span>
              </div>
              {billing === 'yearly' && plan.yearSave && i > 0 && (
                <p className={`font-body text-xs mb-4 ${plan.highlight ? 'text-amber-200' : 'text-amber-600'}`}>{plan.yearSave}</p>
              )}

              <ul className="space-y-3 mb-8 mt-4">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 font-body text-sm">
                    <Check size={16} className={`shrink-0 mt-0.5 ${plan.highlight ? 'text-terracotta-light' : 'text-terracotta'}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => {
                  if (i === 0) nav('/auth?mode=register');
                  else if (i === 1) handlePurchase(billing === 'yearly' ? 'basis_yearly' : 'basis_monthly');
                  else handlePurchase(billing === 'yearly' ? 'premium_yearly' : 'premium_3months');
                }}
                disabled={purchasing !== null}
                className={`w-full rounded-full py-5 text-base font-medium transition-all hover:scale-[1.02] ${plan.highlight ? 'bg-white text-sage hover:bg-white/90' : 'bg-terracotta text-white hover:bg-terracotta-dark shadow-lg shadow-terracotta/20'}`}
                data-testid={`plan-cta-${i}`}
              >
                {purchasing && ((i === 1 && (purchasing === 'basis_monthly' || purchasing === 'basis_yearly')) || (i === 2 && (purchasing === 'premium_3months' || purchasing === 'premium_yearly')))
                  ? <Loader2 size={18} className="animate-spin" />
                  : i === 0 ? (de ? 'Gratis starten' : 'Start Free')
                  : i > 0 && user?.plan === (i === 1 ? 'basis' : 'premium') ? (de ? 'Aktiv' : 'Active')
                  : <><CreditCard size={16} className="mr-2" />{de ? 'Jetzt kaufen' : 'Buy now'}</>}
              </Button>
            </Card>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="comparison-section">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-center mb-12">
          {de ? 'Features im Vergleich' : 'Feature Comparison'}
        </h2>
        <div className="bg-white rounded-2xl border border-border/60 overflow-hidden shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]">
          <div className="grid grid-cols-4 gap-4 p-4 bg-warmgray/50 font-body text-sm font-semibold text-foreground/60">
            <div>Feature</div>
            <div className="text-center">{de ? 'Gratis' : 'Free'}</div>
            <div className="text-center text-sage">Basis</div>
            <div className="text-center">Premium</div>
          </div>
          {comparisons.map((row, i) => (
            <div key={i} className="grid grid-cols-4 gap-4 p-4 border-t border-border/30 items-center" data-testid={`compare-row-${i}`}>
              <div className="flex items-center gap-2 font-body text-sm">
                <row.icon size={16} className="text-terracotta shrink-0" />
                {row.feature}
              </div>
              <div className="text-center font-body text-sm text-foreground/50">
                {row.free === true ? <Check size={16} className="mx-auto text-green-500" /> : row.free === '-' ? <span className="text-foreground/20">–</span> : row.free}
              </div>
              <div className="text-center font-body text-sm">
                {row.basis === true ? <Check size={16} className="mx-auto text-green-500" /> : row.basis === '-' ? <span className="text-foreground/20">–</span> : row.basis}
              </div>
              <div className="text-center font-body text-sm">
                {row.premium === true ? <Check size={16} className="mx-auto text-green-500" /> : row.premium === '-' ? <span className="text-foreground/20">–</span> : row.premium}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16" data-testid="faq-section">
        <h2 className="font-display text-3xl font-medium tracking-tight text-center mb-8">
          {de ? 'Häufige Fragen' : 'FAQ'}
        </h2>
        <div className="space-y-4">
          {(de ? [
            { q: 'Kann ich jederzeit kündigen?', a: 'Ja, du kannst dein Abo jederzeit kündigen. Keine versteckten Kosten, keine Mindestlaufzeit.' },
            { q: 'Was passiert nach der Gratis-Phase?', a: 'Du behältst den Basis-Zugang mit eingeschraenkten Features. Kein automatisches Upgrade.' },
            { q: 'Ist die App für mich geeignet?', a: 'belosophie ist speziell für Frauen 40-65 mit Themen wie Gewicht, Hormone, Hashimoto und Menopause entwickelt.' },
            { q: 'Ersetzt die App meinen Arzt?', a: 'Nein. belosophie unterstützt dich bei Ernährung und Lebensstil, ersetzt aber keine medizinische Beratung. Konsultiere immer deine Ärztin.' },
          ] : [
            { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time. No hidden costs, no minimum term.' },
            { q: 'What happens after the free trial?', a: 'You keep basic access with limited features. No automatic upgrade.' },
            { q: 'Is the app suitable for me?', a: 'belosophie is specifically designed for women 40-65 dealing with weight, hormones, Hashimoto and menopause.' },
            { q: 'Does the app replace my doctor?', a: 'No. belosophie supports you with nutrition and lifestyle but does not replace medical advice. Always consult your doctor.' },
          ]).map((faq, i) => (
            <details key={i} className="bg-white border border-border/60 rounded-2xl p-5 group" data-testid={`faq-${i}`}>
              <summary className="font-display text-lg font-medium cursor-pointer list-none flex items-center justify-between">
                {faq.q}
                <ArrowRight size={16} className="text-foreground/30 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="font-body text-sm text-foreground/60 mt-3 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">
          {de ? 'Bereit für deinen Glow?' : 'Ready for your Glow?'}
        </h2>
        <p className="font-body text-foreground/50 mb-8">
          {de ? 'Starte jetzt kostenlos – keine Kreditkarte erforderlich.' : 'Start free now – no credit card required.'}
        </p>
        <Button
          onClick={() => nav('/auth?mode=register')}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-8 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all"
          data-testid="pricing-final-cta"
        >
          {de ? 'Gratis starten' : 'Start Free'} <ArrowRight className="ml-2" size={20} />
        </Button>
      </section>

      {/* Disclaimer */}
      <footer className="border-t border-border/30 bg-warmgray/30">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center">
          <p className="font-body text-xs text-foreground/40 max-w-2xl mx-auto">
            {de
              ? 'belosophie unterstützt Frauen 40+ bei Ernährung und Lebensstil bei Hashimoto, Perimenopause und Wechseljahren. Keine medizinische Diagnose oder Therapie. Immer Ärztin/Endokrinologin konsultieren. Lab-Werte dienen nur zur Dokumentation, keine Interpretation durch die App.'
              : 'belosophie supports women 40+ with nutrition and lifestyle for Hashimoto, perimenopause and menopause. No medical diagnosis or therapy. Always consult your doctor/endocrinologist. Lab values are for documentation only, no interpretation by the app.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
